import React, { useMemo } from "react";
import { Box, Typography, Skeleton, Tooltip } from "@mui/material";
import CardTitle from "./CardTitle";

/* How many centres the card ranks. */
const TOP_N = 5;

/* One measure (ECG count) across a handful of named centres, so the form is a
   ranked horizontal bar list: horizontal because centre names are long, and a
   single accent hue because the bars encode magnitude, not identity. Bar length
   is relative to the leader, values are labelled directly, so the card needs no
   axis or legend. */
const DashboardTopCentres = ({
  centreData,
  isLoading,
  barsLoading,
  cardSx,
  accent = "#5751E9",
}) => {
  const rows = useMemo(
    () =>
      [...(centreData || [])]
        .filter((centre) => (centre?.total ?? 0) > 0)
        .sort((a, b) => (b?.total ?? 0) - (a?.total ?? 0))
        .slice(0, TOP_N),
    [centreData],
  );

  // Bars are scaled against the leader, so the top centre always fills the track.
  const maxTotal = rows.length ? rows[0].total : 0;

  return (
    <Box
      sx={{
        ...cardSx,
        p: { xs: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardTitle accent={accent}>Top Performing Centres</CardTitle>

      <Typography
        sx={{
          fontSize: 13,
          color: "var(--app-text-muted)",
          fontFamily: "Figtree",
          mt: 0.5,
          mb: 2,
        }}
      >
        By ECG count
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          {Array.from({ length: TOP_N }).map((_, index) => (
            <Box key={index}>
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={8}
                sx={{ borderRadius: "4px", mt: 0.75 }}
              />
            </Box>
          ))}
        </Box>
      ) : rows.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
            // Rows always stack from the top: with only two or three centres,
            // spreading them down the card reads as a broken layout.
            alignContent: "flex-start",
          }}
        >
          {rows.map((centre, index) => {
            const total = centre?.total ?? 0;
            const widthPct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;

            return (
              <Tooltip
                key={centre?.id ?? `${centre?.name}-${index}`}
                arrow
                placement="top"
                title={
                  <Box sx={{ fontFamily: "Figtree", fontSize: 13 }}>
                    <Box sx={{ fontWeight: 700, mb: 0.25 }}>{centre?.name}</Box>
                    <Box>Normal: {centre?.normal ?? 0}</Box>
                    <Box>Abnormal: {centre?.abnormal ?? 0}</Box>
                    <Box>Critical: {centre?.critical ?? 0}</Box>
                    <Box>Retake: {centre?.retake ?? 0}</Box>
                  </Box>
                }
              >
                <Box sx={{ cursor: "default" }}>
                  {/* Name and value share a line; the value sits at the bar's end
                      so nothing has to be read off an axis. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--app-text)",
                        fontFamily: "Figtree",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: "var(--app-text-muted)", mr: 0.75 }}
                      >
                        {index + 1}.
                      </Box>
                      {centre?.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--app-text)",
                        fontFamily: "Figtree",
                        flexShrink: 0,
                      }}
                    >
                      {Number(total).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Track + fill. Square at the baseline, rounded at the data end. */}
                  <Box
                    aria-hidden="true"
                    sx={{
                      mt: 0.75,
                      height: 8,
                      width: "100%",
                      borderRadius: "4px",
                      backgroundColor: "var(--app-border)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${widthPct}%`,
                        minWidth: widthPct > 0 ? 4 : 0,
                        backgroundColor: accent,
                        borderRadius: "0 4px 4px 0",
                        transition: "width .45s ease",
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 220,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={barsLoading} alt="No data" width={130} height={130} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: "var(--app-text-muted)",
              mt: 1,
              fontFamily: "Figtree",
            }}
          >
            No Centre Data
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DashboardTopCentres;
